import * as fromFeedActions from '../core/state/feed.actions';

import { ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { catchError, filter, map, take, tap } from 'rxjs/operators';

import { AppState } from '../core/state/app.state';
import { Feed } from '../core/models/core.models';
import { FeedEntityService } from '../core/services/feed-entity.service';
import { WorkspaceObjectsService } from '../core/services/workspace-objects.service';
import { Injectable } from '@angular/core';
import { LoggerService } from '@services/logger.service';
import { TranslateService } from '@ngx-translate/core';
import { ApiErrorPresenterService } from 'src/app/core/services/api-error-presenter.service';
import { PageLoadErrorService } from 'src/app/core/services/page-load-error.service';
import { augmentObjectWithIconAndImage } from '../shared/utils/object-utils';

@Injectable({ providedIn: 'root' })
export class FeedResolver implements Resolve<Feed | null> {
  constructor(
    private store: Store<AppState>,
    private feedEntity: FeedEntityService,
    private workspaceObjects: WorkspaceObjectsService,
    private logger: LoggerService,
    private presenter: ApiErrorPresenterService,
    private translate: TranslateService,
    private pageLoadError: PageLoadErrorService,
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<Feed | null> {
    const feedId = route.paramMap.get('feedId');
    if (!feedId) {
      this.logger.error('No feedId in route');
      return of(null);
    }

    return this.loadFeedDirectly(feedId);
  }

  private loadFeedDirectly(feedId: string): Observable<Feed | null> {
    return this.feedEntity.getFeed(feedId).pipe(
      // 1) transform the raw Feed
      filter((feed) => !!feed),
      map((feed) => augmentObjectWithIconAndImage(feed)), // returns Feed

      // 2) dispatch the success into NgRx, and mirror the fresh feed into the
      //    workspace-objects cache so its list row shows current fields
      tap((feed) => {
        this.store.dispatch(
          fromFeedActions.loadFeedSuccess({ feed, setCurrentFeed: true }),
        );
        this.workspaceObjects.upsertFeed(feed);
      }),

      // if anything errors (store-select never will, but HTTP can),
      // catch it here and emit null
      catchError((err) => {
        this.logger.error('Failed to load feed', err);
        this.presenter.present(err, {
          inline: this.pageLoadError.set,
          fallback: this.translate.instant(
            'feed.msg.the-feed-could-not-be-loaded',
          ),
        });
        return of<null>(null);
      }),

      // finish the whole stream after the first emission
      take(1),
    );
  }
}
